import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BreadcrumbSegment {
  label: string;
  path?: string;
}

/**
 * Route-to-breadcrumb mapping.
 * Each entry maps a route pattern to its breadcrumb segments.
 * The section label comes from i18n nav.section.* keys.
 * Dynamic segments (:id, :transactionId) get replaced with actual URL values.
 */
interface RouteConfig {
  sectionKey: string;
  parentLabel: string;
  parentPath: string;
}

const routeConfigs: Record<string, RouteConfig> = {
  '/transactions': { sectionKey: 'nav.section.operations', parentLabel: 'nav.transactions', parentPath: '/transactions' },
  '/subscriptions': { sectionKey: 'nav.section.operations', parentLabel: 'nav.subscriptions', parentPath: '/subscriptions' },
  '/webhooks': { sectionKey: 'nav.section.operations', parentLabel: 'nav.webhookEvents', parentPath: '/webhooks' },
  '/merchants': { sectionKey: 'nav.section.configuration', parentLabel: 'nav.merchants', parentPath: '/merchants' },
  '/services': { sectionKey: 'nav.section.configuration', parentLabel: 'nav.services', parentPath: '/services' },
  '/webhooks/config': { sectionKey: 'nav.section.configuration', parentLabel: 'nav.webhookConfig', parentPath: '/webhooks/config' },
  '/iq-services': { sectionKey: 'nav.section.platform', parentLabel: 'nav.gatewayConfig', parentPath: '/iq-services' },
};

/**
 * Resolves the current pathname to breadcrumb segments.
 * Returns null for dashboard (/) — no breadcrumbs needed there.
 */
function resolveBreadcrumbs(pathname: string, t: (key: string) => string): BreadcrumbSegment[] | null {
  if (pathname === '/') return null;

  // Try exact match first (e.g. /webhooks/config)
  const exactConfig = routeConfigs[pathname];
  if (exactConfig) {
    return [
      { label: t(exactConfig.sectionKey) },
      { label: t(exactConfig.parentLabel) },
    ];
  }

  // Try prefix matching for detail/sub pages
  // Sort by specificity (longer prefixes first) to match /webhooks/config before /webhooks
  const sortedPrefixes = Object.keys(routeConfigs).sort((a, b) => b.length - a.length);

  for (const prefix of sortedPrefixes) {
    if (pathname.startsWith(prefix + '/')) {
      const config = routeConfigs[prefix];
      const remainder = pathname.slice(prefix.length + 1);

      // Known sub-pages with translatable labels
      const subPageLabels: Record<string, Record<string, string>> = {
        '/transactions': { trigger: 'Transaction Trigger' },
        '/merchants': { new: t('button.create') },
        '/iq-services': { new: t('button.create') },
      };

      const subLabel = subPageLabels[prefix]?.[remainder] ?? remainder;

      return [
        { label: t(config.sectionKey) },
        { label: t(config.parentLabel), path: config.parentPath },
        { label: subLabel },
      ];
    }
  }

  return null;
}

/**
 * Breadcrumb navigation in the header.
 * Shows current location context based on route hierarchy.
 * Hidden on mobile (< md breakpoint).
 *
 * @see Spec 110 — Breadcrumbs einfuehren
 */
export function Breadcrumb() {
  const location = useLocation();
  const { t } = useTranslation();

  const segments = resolveBreadcrumbs(location.pathname, t);

  if (!segments || segments.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="hidden items-center gap-1 text-sm md:flex">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        return (
          <span key={index} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" aria-hidden="true" />
            )}
            {segment.path && !isLast ? (
              <Link
                to={segment.path}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                {segment.label}
              </Link>
            ) : (
              <span className={isLast ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                {segment.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
